<div class="mx-auto max-w-3xl px-4 py-6 text-sm text-slate-800">
  <div class="mb-4 flex items-center justify-between gap-4">
    <h1 class="m-0 text-lg font-semibold">Debug · Store</h1>
    <div class="flex items-center gap-2">
      <button type="button"
        class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
        [disabled]="loading || nuking"
        (click)="refresh()">
        <i class="ms-Icon ms-Icon--Refresh" aria-hidden="true"></i>
        Refresh
      </button>
      <button type="button"
        class="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100 disabled:opacity-50"
        [disabled]="loading || nuking"
        (click)="nuke()">
        <i class="ms-Icon ms-Icon--Delete" aria-hidden="true"></i>
        {{ nuking ? 'Nuking…' : 'Nuke local cache' }}
      </button>
      <button type="button"
        class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
        (click)="reloadPage()">
        <i class="ms-Icon ms-Icon--PageArrowRight" aria-hidden="true"></i>
        Reload page
      </button>
    </div>
  </div>

  @if (lastError) {
    <div class="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800">
      <strong>Error:</strong> {{ lastError }}
    </div>
  }

  @if (lastNukeAt) {
    <div class="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
      Local cache wiped at <code>{{ lastNukeAt }}</code>. Click <strong>Reload page</strong> to test the cold-start path.
    </div>
  }

  <section class="mb-6 rounded-md border border-slate-200 bg-white">
    <header class="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Backend</header>
    <dl class="grid grid-cols-2 gap-y-1 px-3 py-2">
      <dt class="text-slate-500">Storage mode</dt>
      <dd><code>{{ mode || '—' }}</code></dd>
      <dt class="text-slate-500">Worker available</dt>
      <dd>
        @if (workerProbe) {
          <span>
            {{ workerProbe.worker ? 'yes' : 'no' }}
            <span class="text-slate-500">
              / OPFS {{ workerProbe.opfsAvailable ? 'yes' : 'no' }}
              / SQLite {{ workerProbe.sqliteProbeOk ? 'yes' : 'no' }}
            </span>
          </span>
        } @else {
          <span class="text-slate-400">probingâ€¦</span>
        }
      </dd>
      @if (workerProbe?.sqliteProbeError) {
        <dt class="text-slate-500">Worker probe error</dt>
      }
      @if (workerProbe?.sqliteProbeError) {
        <dd class="break-all text-amber-800">{{ workerProbe?.sqliteProbeError }}</dd>
      }
      <dt class="text-slate-500">OPFS file size</dt>
      <dd>{{ formatBytes(stats?.fileSizeBytes) }}</dd>
      <dt class="text-slate-500">Browser batch probe</dt>
      <dd>
        <div class="flex flex-wrap items-center gap-2">
          <select class="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
            [(ngModel)]="batchProbeEnvironment"
            [disabled]="batchProbeLoading || !envs.length">
            @for (env of envs; track env) {
              <option [value]="env.name">{{ env.displayName || env.name }}</option>
            }
          </select>
          <label class="inline-flex items-center gap-1 text-xs text-slate-600">
            <input type="checkbox" [(ngModel)]="batchProbeAdmin" [disabled]="batchProbeLoading">
            admin
          </label>
          <button type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
            [disabled]="batchProbeLoading || !envs.length"
            (click)="runBatchProbe()">
            {{ batchProbeLoading ? 'Probing…' : 'Probe' }}
          </button>
        </div>
        @if (batchProbeResult) {
          <div class="mt-1 break-all text-xs text-slate-600">{{ batchProbeResult }}</div>
        }
      </dd>
      <dt class="text-slate-500">Browser data cache probe</dt>
      <dd>
        <div class="flex flex-wrap items-center gap-2">
          <select class="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
            [(ngModel)]="batchProbeEnvironment"
            [disabled]="browserDataCacheProbeLoading || !envs.length">
            @for (env of envs; track env) {
              <option [value]="env.name">{{ env.displayName || env.name }}</option>
            }
          </select>
          <select class="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
            [(ngModel)]="browserDataCacheProbeSourceMode"
            [disabled]="browserDataCacheProbeLoading">
            <option value="cached">cached summaries</option>
            <option value="live">live API</option>
          </select>
          <button type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
            [disabled]="browserDataCacheProbeLoading || browserDataCacheResetting || !envs.length"
            (click)="runBrowserDataCacheProbe()">
            {{ browserDataCacheProbeLoading ? 'Probing...' : 'Probe package cache' }}
          </button>
          <button type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
            [disabled]="browserDataCacheStatusLoading || !envs.length"
            (click)="checkBrowserDataCacheProductionStatus()">
            {{ browserDataCacheStatusLoading ? 'Checking...' : 'Production status' }}
          </button>
          <button type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
            [disabled]="browserDataCacheProbeLoading || browserDataCacheResetting"
            (click)="resetBrowserDataCacheDebugDb()">
            {{ browserDataCacheResetting ? 'Resetting...' : 'Reset debug DB' }}
          </button>
          <button type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-100 disabled:opacity-50"
            [disabled]="browserDataCacheProductionResetting"
            (click)="resetBrowserDataCacheProductionDb()">
            {{ browserDataCacheProductionResetting ? 'Resetting...' : 'Reset production mirror' }}
          </button>
        </div>
        @if (browserDataCacheProbeResult) {
          <div class="mt-1 break-all text-xs text-slate-600">{{ browserDataCacheProbeResult }}</div>
        }
        @if (browserDataCacheProductionStatusResult) {
          <div class="mt-1 break-all text-xs text-slate-600">{{ browserDataCacheProductionStatusResult }}</div>
        }
      </dd>
    </dl>
  </section>

  <!-- Master: tables list with click-to-select. -->
  <section class="mb-6 rounded-md border border-slate-200 bg-white">
    <header class="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
      Tables — click a row to inspect
    </header>
    <table class="w-full text-left">
      <thead>
        <tr class="border-b border-slate-100 text-xs text-slate-500">
          <th class="px-3 py-2 font-medium">Table</th>
          <th class="px-3 py-2 text-right font-medium">Rows</th>
          <th class="px-3 py-2 font-medium">Notes</th>
        </tr>
      </thead>
      <tbody>
        @for (row of tableRows; track row) {
          <tr
            class="border-b border-slate-100 last:border-0 transition-colors"
            [class.cursor-pointer]="hasDetail(row.name)"
            [class.cursor-default]="!hasDetail(row.name)"
            [class.bg-blue-50]="selectedTable === row.name"
            [class.hover:bg-slate-50]="selectedTable !== row.name && hasDetail(row.name)"
            (click)="hasDetail(row.name) && selectTable(row.name)">
            <td class="px-3 py-1.5">
              <code [class.font-semibold]="selectedTable === row.name">{{ row.name }}</code>
            </td>
            <td class="px-3 py-1.5 text-right tabular-nums">
              @if (row.count >= 0) {
                <span>{{ row.count }}</span>
              }
              @if (row.count < 0) {
                <span class="text-slate-400" title="table missing">—</span>
              }
            </td>
            <td class="px-3 py-1.5 text-xs text-slate-500">
              @if (row.name === 'user_aliases') {
                <span>
                  Secondary email→user index. Resolves any alias to <code>users.key</code> in one indexed lookup.
                </span>
              }
              @if (row.name === 'configs') {
                <span>Key/value bag — settings, flags (e.g. <code>dexieCleanupDone</code>).</span>
              }
              @if (row.name === 'environments') {
                <span>Per-env scan state — separate timestamps for flows, admin flows, connections (user/admin).</span>
              }
              @if (row.name === 'flows' || row.name === 'flows_admin') {
                <span>Cached flows for current OID.</span>
              }
              @if (row.name === 'runs') {
                <span>Cached flow runs.</span>
              }
              @if (row.name === 'users') {
                <span>Cached Power Platform users.</span>
              }
              @if (row.name === 'connections') {
                <span>Cached connections (user + admin scope) per environment.</span>
              }
              @if (!hasDetail(row.name)) {
                <span class="ml-1 text-slate-400">(no drill-down yet)</span>
              }
            </td>
          </tr>
        }
        @if (tableRows.length === 0) {
          <tr>
            <td colspan="3" class="px-3 py-2 text-slate-400">No table data.</td>
          </tr>
        }
      </tbody>
    </table>
  </section>

  <!-- Detail: only the selected table's drill-down. -->
  <section class="mb-6 rounded-md border border-slate-200 bg-white">
    <header class="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
      <code>{{ selectedTable }}</code>
      @if (selectedTable === 'environments') {
        <span> — {{ envs.length }} rows</span>
      }
      @if (selectedTable === 'configs') {
        <span> — {{ configs.length }} rows</span>
      }
      @if (selectedTable === 'users') {
        <span> — {{ users.length }} rows</span>
      }
      @if (selectedTable === 'user_aliases') {
        <span> — {{ userAliases.length }} rows</span>
      }
      @if (selectedTable === 'connections') {
        <span> — {{ connections.length }} rows</span>
      }
    </header>

    <!-- environments — one column per scan source so freshness is auditable. -->
    @if (selectedTable === 'environments') {
      <table class="w-full text-left">
        <thead>
          <tr class="border-b border-slate-100 text-xs text-slate-500">
            <th class="px-3 py-2 font-medium">Display name</th>
            <th class="px-3 py-2 font-medium">name</th>
            <th class="px-3 py-2 text-right font-medium">Flows</th>
            <th class="px-3 py-2 text-right font-medium" title="Last user-scope flow scan">Flows scanned</th>
            <th class="px-3 py-2 text-right font-medium" title="Last admin-scope flow scan">Admin flows scanned</th>
            <th class="px-3 py-2 text-right font-medium" title="Last user-scope connections fetch">Connections scanned</th>
            <th class="px-3 py-2 text-right font-medium" title="Last admin-scope connections fetch">Admin connections scanned</th>
            <th class="px-3 py-2 text-right font-medium">Duration</th>
          </tr>
        </thead>
        <tbody>
          @for (e of envs; track e) {
            <tr class="border-b border-slate-100 last:border-0">
              <td class="px-3 py-1.5">{{ e.displayName || '(no name)' }}</td>
              <td class="px-3 py-1.5"><code class="text-xs">{{ e.name }}</code></td>
              <td class="px-3 py-1.5 text-right tabular-nums">{{ e.flowCount ?? '—' }}</td>
              <td class="px-3 py-1.5 text-right tabular-nums" [title]="formatTimestamp(e.scanCompletedAt)">
                {{ formatAgeFromMs(e.scanCompletedAt) }}
              </td>
              <td class="px-3 py-1.5 text-right tabular-nums" [title]="formatTimestamp(e.flowsAdminScannedAt)">
                {{ formatAgeFromMs(e.flowsAdminScannedAt) }}
              </td>
              <td class="px-3 py-1.5 text-right tabular-nums" [title]="formatTimestamp(e.connectionsScannedAt)">
                {{ formatAgeFromMs(e.connectionsScannedAt) }}
              </td>
              <td class="px-3 py-1.5 text-right tabular-nums" [title]="formatTimestamp(e.connectionsAdminScannedAt)">
                {{ formatAgeFromMs(e.connectionsAdminScannedAt) }}
              </td>
              <td class="px-3 py-1.5 text-right tabular-nums">{{ formatDuration(e.durationMs) }}</td>
            </tr>
          }
          @if (envs.length === 0) {
            <tr>
              <td colspan="8" class="px-3 py-2 text-slate-400">No environments cached. Sign in and load <code>/flows</code>, or scan an environment.</td>
            </tr>
          }
        </tbody>
      </table>
    }

    <!-- configs -->
    @if (selectedTable === 'configs') {
      <table class="w-full text-left">
        <thead>
          <tr class="border-b border-slate-100 text-xs text-slate-500">
            <th class="px-3 py-2 font-medium">Key</th>
            <th class="px-3 py-2 font-medium">Value</th>
          </tr>
        </thead>
        <tbody>
          @for (c of configs; track c) {
            <tr class="border-b border-slate-100 last:border-0 align-top">
              <td class="px-3 py-1.5 whitespace-nowrap"><code>{{ c.key }}</code></td>
              <td class="px-3 py-1.5 break-all font-mono text-xs text-slate-600">{{ formatValue(c.value) }}</td>
            </tr>
          }
          @if (configs.length === 0) {
            <tr>
              <td colspan="2" class="px-3 py-2 text-slate-400">No configs cached.</td>
            </tr>
          }
        </tbody>
      </table>
    }

    <!-- users — key is `{tenantId}.{oid}`. emails[] is primary-first, deduped. -->
    @if (selectedTable === 'users') {
      <table class="w-full text-left">
        <thead>
          <tr class="border-b border-slate-100 text-xs text-slate-500">
            <th class="px-3 py-2 font-medium">Display name</th>
            <th class="px-3 py-2 font-medium">Emails (primary first)</th>
            <th class="px-3 py-2 font-medium">key</th>
          </tr>
        </thead>
        <tbody>
          @for (u of users; track u) {
            <tr class="border-b border-slate-100 last:border-0 align-top">
              <td class="px-3 py-1.5">
                @if (u.displayName) {
                  <span>{{ u.displayName }}</span>
                }
                @if (!u.displayName) {
                  <span class="text-slate-400 italic">(no name)</span>
                }
              </td>
              <td class="px-3 py-1.5">
                @if (u.emails?.length) {
                  <span>{{ u.emails!.join(', ') }}</span>
                }
                @if (!u.emails?.length) {
                  <span class="text-slate-400">—</span>
                }
              </td>
              <td class="px-3 py-1.5"><code class="text-xs">{{ u.key }}</code></td>
            </tr>
          }
          @if (users.length === 0) {
            <tr>
              <td colspan="3" class="px-3 py-2 text-slate-400">No users cached.</td>
            </tr>
          }
        </tbody>
      </table>
    }

    <!-- user_aliases -->
    @if (selectedTable === 'user_aliases') {
      <table class="w-full text-left">
        <thead>
          <tr class="border-b border-slate-100 text-xs text-slate-500">
            <th class="px-3 py-2 font-medium">Email</th>
            <th class="px-3 py-2 font-medium">→ user_key</th>
          </tr>
        </thead>
        <tbody>
          @for (a of userAliases; track a) {
            <tr class="border-b border-slate-100 last:border-0">
              <td class="px-3 py-1.5">{{ a.email }}</td>
              <td class="px-3 py-1.5"><code class="text-xs">{{ a.user_key }}</code></td>
            </tr>
          }
          @if (userAliases.length === 0) {
            <tr>
              <td colspan="2" class="px-3 py-2 text-slate-400">No user_aliases. Sign in and load users.</td>
            </tr>
          }
        </tbody>
      </table>
    }

    <!-- connections — cached per environment, sources tracks user/admin scope visibility. -->
    @if (selectedTable === 'connections') {
      <table class="w-full text-left">
        <thead>
          <tr class="border-b border-slate-100 text-xs text-slate-500">
            <th class="px-3 py-2 font-medium">Display name</th>
            <th class="px-3 py-2 font-medium">name</th>
            <th class="px-3 py-2 font-medium">apiId</th>
            <th class="px-3 py-2 font-medium">environment</th>
            <th class="px-3 py-2 font-medium" title="Origin tags — a connection visible in both user and admin scope shows both">Sources</th>
          </tr>
        </thead>
        <tbody>
          @for (c of connections; track c) {
            <tr class="border-b border-slate-100 last:border-0 align-top">
              <td class="px-3 py-1.5">
                @if (c.displayName) {
                  <span>{{ c.displayName }}</span>
                }
                @if (!c.displayName) {
                  <span class="text-slate-400 italic">(no name)</span>
                }
              </td>
              <td class="px-3 py-1.5"><code class="text-xs">{{ c.name }}</code></td>
              <td class="px-3 py-1.5"><code class="text-xs">{{ c.apiId }}</code></td>
              <td class="px-3 py-1.5"><code class="text-xs">{{ c.environment }}</code></td>
              <td class="px-3 py-1.5">
                @if (c.sources?.length) {
                  <span><code class="text-xs">{{ c.sources!.join(', ') }}</code></span>
                }
                @if (!c.sources?.length) {
                  <span class="text-slate-400">—</span>
                }
              </td>
            </tr>
          }
          @if (connections.length === 0) {
            <tr>
              <td colspan="5" class="px-3 py-2 text-slate-400">No connections cached. Visit <code>/connections</code> to populate.</td>
            </tr>
          }
        </tbody>
      </table>
    }

    <!-- Fallback for tables without a drill-down. -->
    @if (!hasDetail(selectedTable)) {
      <div class="px-3 py-3 text-xs text-slate-500">
        No drill-down view for <code>{{ selectedTable }}</code> yet.
      </div>
    }
  </section>
</div>
